import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  CardActions,
  Chip,
  useTheme,
  useMediaQuery,
  Stack,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { SEOHead, pageSEO } from '../../utils/seo';
import {
  Code as CodeIcon,
  Speed as SpeedIcon,
  Security as SecurityIcon,
  Support as SupportIcon,
  Devices as DevicesIcon,
  CloudDone as CloudIcon,
  ArrowForward as ArrowForwardIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
} from '@mui/icons-material';
import { usePagePerformance } from '../../utils/performance';
import SpecificationFlowAnimation from './SpecificationFlowAnimation';

const HomePage: React.FC = () => {
  // Monitor page performance for constitutional compliance
  usePagePerformance('HomePage');
  
  const { t } = useTranslation('marketing');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Service data for the homepage
  const services = [
    {
      id: 'web-app',
      title: t('service_web_app'),
      description: t('service_web_app_desc'),
      icon: <CodeIcon fontSize="large" />,
      features: ['React 18+', 'Laravel 10+', 'TypeScript', 'Material-UI'],
      testId: 'service-web-app',
    },
    {
      id: 'mobile-app',
      title: t('service_mobile_app'),
      description: t('service_mobile_app_desc'),
      icon: <DevicesIcon fontSize="large" />,
      features: ['React Native', 'Flutter', 'iOS & Android', 'Cross-platform'],
      testId: 'service-mobile-app',
    },
    {
      id: 'api-development',
      title: t('service_api'),
      description: t('service_api_desc'),
      icon: <CloudIcon fontSize="large" />,
      features: ['REST APIs', 'Microservices', 'Documentation', 'Testing'],
      testId: 'service-api',
    },
    {
      id: 'devops',
      title: t('service_devops'),
      description: t('service_devops_desc'),
      icon: <SecurityIcon fontSize="large" />,
      features: ['Docker', 'CI/CD', 'AWS/Azure', 'Monitoring'],
      testId: 'service-devops',
    },
  ];

  const navigate = useNavigate();

  const handleContactClick = () => {
    navigate('/contact');
  };

  const handleServiceClick = (serviceId: string) => {
    navigate('/services', { state: { selectedService: serviceId } });
  };

  return (
    <>
      <SEOHead {...pageSEO.home} />
      <Box component="main" id="main-content" tabIndex={-1}>
      {/* Hero Section */}
      <Box
        component="section"
        data-testid="hero-section"
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          color: 'white',
          py: { xs: 8, md: 12 },
          mb: 8,
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: 'center', gap: 4 }}>
            <Box sx={{ flex: { xs: 1, md: '1 1 58%' } }}>
              <Typography
                variant="h1"
                component="h1"
                data-testid="hero-title"
                sx={{
                  fontSize: { xs: '2.5rem', md: '3.5rem' },
                  fontWeight: 700,
                  mb: 2,
                  lineHeight: 1.2,
                }}
              >
                {t('hero_main_title')}
              </Typography>
              <Typography
                variant="h4"
                component="h2"
                data-testid="hero-subtitle"
                sx={{
                  fontSize: { xs: '1.25rem', md: '1.75rem' },
                  fontWeight: 400,
                  mb: 3,
                  opacity: 0.9,
                }}
              >
                {t('hero_tagline')}
              </Typography>
              <Typography
                variant="body1"
                data-testid="hero-description"
                sx={{
                  fontSize: { xs: '1rem', md: '1.125rem' },
                  mb: 4,
                  maxWidth: 600,
                  opacity: 0.8,
                  lineHeight: 1.6,
                }}
              >
                {t('hero_description')}
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                <Button
                  variant="contained"
                  size="large"
                  data-testid="contact-cta"
                  onClick={handleContactClick}
                  sx={{
                    bgcolor: 'white',
                    color: theme.palette.primary.main,
                    px: 4,
                    py: 1.5,
                    fontSize: '1.1rem',
                    fontWeight: 600,
                    '&:hover': {
                      bgcolor: 'grey.100',
                    },
                  }}
                  endIcon={<ArrowForwardIcon />}
                >
                  {t('cta_start_project')}
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  data-testid="services-cta"
                  sx={{
                    borderColor: 'white',
                    color: 'white',
                    px: 4,
                    py: 1.5,
                    fontSize: '1.1rem',
                    fontWeight: 600,
                    '&:hover': {
                      borderColor: 'grey.100',
                      bgcolor: 'rgba(255,255,255,0.1)',
                    },
                  }}
                >
                  {t('cta_view_services')}
                </Button>
              </Box>
            </Box>
            <Box sx={{ flex: { xs: 1, md: '1 1 42%' } }}>
              <Box
                data-testid="hero-visual"
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: { xs: 300, md: 400 },
                  borderRadius: 2,
                  overflow: 'hidden',
                }}
              >
                <SpecificationFlowAnimation />
              </Box>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Services Section */}
      <Container maxWidth="lg">
        <Box component="section" data-testid="services-section" sx={{ mb: 8 }}>
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography
              variant="h2"
              component="h2"
              data-testid="services-title"
              sx={{
                fontSize: { xs: '2rem', md: '2.75rem' },
                fontWeight: 700,
                mb: 2,
                color: 'text.primary',
              }}
            >
              {t('services_title')}
            </Typography>
            <Typography
              variant="h6"
              data-testid="services-subtitle"
              sx={{
                color: 'text.secondary',
                maxWidth: 600,
                mx: 'auto',
                lineHeight: 1.6,
              }}
            >
              {t('services_description')}
            </Typography>
          </Box>

          <Box 
            sx={{ 
              display: 'grid', 
              gridTemplateColumns: { 
                xs: '1fr', 
                sm: 'repeat(2, 1fr)', 
                lg: 'repeat(4, 1fr)' 
              }, 
              gap: 4 
            }}
          >
            {services.map((service) => (
              <Card
                key={service.id}
                data-testid={service.testId}
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: theme.shadows[8],
                  },
                }}
              >
                <CardContent sx={{ flexGrow: 1, p: 3 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 64,
                      height: 64,
                      borderRadius: '50%',
                      bgcolor: `${theme.palette.primary.main}20`,
                      color: theme.palette.primary.main,
                      mb: 3,
                    }}
                  >
                    {service.icon}
                  </Box>
                  <Typography
                    variant="h6"
                    component="h3"
                    data-testid={`${service.testId}-title`}
                    sx={{ fontWeight: 600, mb: 2 }}
                  >
                    {service.title}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    data-testid={`${service.testId}-description`}
                    sx={{ mb: 3, lineHeight: 1.6 }}
                  >
                    {service.description}
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {service.features.map((feature) => (
                      <Chip
                        key={feature}
                        label={feature}
                        size="small"
                        variant="outlined"
                        sx={{ fontSize: '0.75rem' }}
                      />
                    ))}
                  </Box>
                </CardContent>
                <CardActions sx={{ p: 3, pt: 0 }}>
                  <Button
                    size="small"
                    data-testid={`${service.testId}-cta`}
                    onClick={() => handleServiceClick(service.id)}
                    sx={{ fontWeight: 600 }}
                  >
                    {t('learn_more')}
                    <ArrowForwardIcon sx={{ ml: 1, fontSize: 16 }} />
                  </Button>
                </CardActions>
              </Card>
            ))}
          </Box>
        </Box>

        {/* Process Section */}
        <Box component="section" data-testid="process-section" sx={{ mb: 8 }}>
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography
              variant="h2"
              component="h2"
              data-testid="process-title"
              sx={{
                fontSize: { xs: '2rem', md: '2.75rem' },
                fontWeight: 700,
                mb: 2,
                color: 'text.primary',
              }}
            >
              {t('process_title')}
            </Typography>
            <Typography
              variant="h6"
              data-testid="process-subtitle"
              sx={{
                color: 'text.secondary',
                maxWidth: 600,
                mx: 'auto',
                lineHeight: 1.6,
              }}
            >
              {t('process_subtitle')}
            </Typography>
          </Box>

          <Box 
            sx={{ 
              display: 'grid', 
              gridTemplateColumns: { 
                xs: '1fr', 
                sm: 'repeat(2, 1fr)', 
                md: 'repeat(4, 1fr)' 
              }, 
              gap: 4 
            }}
          >
            {[
              {
                step: '01',
                title: t('process_step_1'),
                description: t('process_step_1_desc'),
                icon: <SupportIcon fontSize="large" />,
              },
              {
                step: '02',
                title: t('process_step_2'),
                description: t('process_step_2_desc'),
                icon: <CodeIcon fontSize="large" />,
              },
              {
                step: '03',
                title: t('process_step_3'),
                description: t('process_step_3_desc'),
                icon: <SpeedIcon fontSize="large" />,
              },
              {
                step: '04',
                title: t('process_step_4'),
                description: t('process_step_4_desc'),
                icon: <CloudIcon fontSize="large" />,
              },
            ].map((process, index) => (
              <Box
                key={index}
                data-testid={`process-step-${index + 1}`}
                sx={{
                  textAlign: 'center',
                  height: '100%',
                }}
              >
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 700,
                    color: theme.palette.primary.main,
                    mb: 2,
                    opacity: 0.8,
                  }}
                >
                  {process.step}
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 64,
                    height: 64,
                    borderRadius: '50%',
                    bgcolor: `${theme.palette.primary.main}20`,
                    color: theme.palette.primary.main,
                    mx: 'auto',
                    mb: 3,
                  }}
                >
                  {process.icon}
                </Box>
                <Typography
                  variant="h6"
                  component="h3"
                  sx={{ fontWeight: 600, mb: 2 }}
                >
                  {process.title}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ lineHeight: 1.6 }}
                >
                  {process.description}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>

        {/* CTA Section */}
        <Box
          component="section"
          data-testid="cta-section"
          sx={{
            textAlign: 'center',
            py: 8,
            px: 4,
            bgcolor: 'grey.50',
            borderRadius: 2,
            mb: 8,
          }}
        >
          <Typography
            variant="h3"
            component="h2"
            data-testid="cta-title"
            sx={{
              fontSize: { xs: '1.75rem', md: '2.25rem' },
              fontWeight: 700,
              mb: 2,
              color: 'text.primary',
            }}
          >
            {t('cta_ready')}
          </Typography>
          <Typography
            variant="h6"
            data-testid="cta-subtitle"
            sx={{
              color: 'text.secondary',
              mb: 4,
              maxWidth: 600,
              mx: 'auto',
              lineHeight: 1.6,
            }}
          >
            {t('cta_ready_desc')}
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              size="large"
              data-testid="cta-contact"
              onClick={handleContactClick}
              sx={{
                px: 4,
                py: 1.5,
                fontSize: '1.1rem',
                fontWeight: 600,
              }}
              endIcon={<ArrowForwardIcon />}
            >
              {t('cta_get_started')}
            </Button>
          </Box>
        </Box>
      </Container>

      {/* Footer */}
      <Box
        component="footer"
        data-testid="footer"
        sx={{
          bgcolor: 'grey.900',
          color: 'white',
          py: 6,
          mt: 8,
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 4 }}>
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="h6"
                component="h3"
                sx={{ fontWeight: 600, mb: 2 }}
              >
                {t('company_name')}
              </Typography>
              <Typography
                variant="body2"
                sx={{ mb: 3, opacity: 0.8, lineHeight: 1.6 }}
              >
                {t('footer_description')}
              </Typography>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  variant="outlined"
                  size="small"
                  data-testid="footer-email"
                  href="mailto:contact@vegadigitalsolutions.com"
                  sx={{
                    borderColor: 'grey.600',
                    color: 'white',
                    '&:hover': {
                      borderColor: 'grey.400',
                    },
                  }}
                  startIcon={<EmailIcon />}
                >
                  contact@vegadigitalsolutions.com
                </Button>
              </Box>
            </Box>
            <Box sx={{ flex: 1 }}>
              <Box sx={{ display: 'flex', gap: 4 }}>
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 600, mb: 2 }}
                  >
                    {t('footer_services')}
                  </Typography>
                  <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0 }}>
                    {services.map((service) => (
                      <Box component="li" key={service.id} sx={{ mb: 1 }}>
                        <Typography
                          variant="body2"
                          component="a"
                          href="#"
                          onClick={(e) => {
                            e.preventDefault();
                            handleServiceClick(service.id);
                          }}
                          sx={{
                            color: 'grey.400',
                            textDecoration: 'none',
                            '&:hover': {
                              color: 'white',
                            },
                          }}
                        >
                          {service.title}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 600, mb: 2 }}
                  >
                    {t('footer_company')}
                  </Typography>
                  <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0 }}>
                    {[
                      { key: 'about', label: t('footer_about'), path: '/about' },
                      { key: 'contact', label: t('footer_contact'), path: '/contact' },
                      { key: 'privacy', label: t('footer_privacy'), path: '/privacy' },
                      { key: 'terms', label: t('footer_terms'), path: '/terms' }
                    ].map((item) => (
                      <Box component="li" key={item.key} sx={{ mb: 1 }}>
                        <Typography
                          variant="body2"
                          component="a"
                          href="#"
                          onClick={(e) => {
                            e.preventDefault();
                            navigate(item.path);
                          }}
                          sx={{
                            color: 'grey.400',
                            textDecoration: 'none',
                            cursor: 'pointer',
                            '&:hover': {
                              color: 'white',
                            },
                          }}
                        >
                          {item.label}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
          <Box
            sx={{
              borderTop: '1px solid',
              borderColor: 'grey.700',
              pt: 4,
              mt: 4,
              textAlign: 'center',
            }}
          >
            <Typography variant="body2" sx={{ opacity: 0.6 }}>
              {t('footer_copyright')}
            </Typography>
          </Box>
        </Container>
      </Box>
    </Box>
    </>
  );
};

export default HomePage;